import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    HeadingLarge,
    HeadingMedium,
} from "@khanacademy/wonder-blocks-typography";
import {mediaQueries} from "@khan/shared-styles";
import {cssUrl} from "@khan/urls";

import {PAGE_WIDTH} from "./styles.ts";
import type {Images, Strings} from "./types.ts";

import checkIcon from "@phosphor-icons/core/regular/check.svg";

type Props = {
    strings: Strings;
    images: Images;
};

const Section = ({
    title,
    description,
}: {
    title: string;
    description: string;
}) => {
    const descriptionParagraphs = description.split("\n");

    return (
        <React.Fragment>
            <HeadingLarge>{title}</HeadingLarge>
            <Strut size={spacing.large_24} />
            {descriptionParagraphs.map((desc, i) => (
                <Body
                    style={
                        i + 1 < descriptionParagraphs.length &&
                        styles.withBottomPadding
                    }
                    key={i}
                >
                    {desc}
                </Body>
            ))}
            <Strut size={spacing.large_24} />
        </React.Fragment>
    );
};

export default class Overview extends React.Component<Props> {
    render(): React.ReactNode {
        const {strings, images} = this.props;

        return (
            <View style={styles.overview}>
                <View style={styles.subsection1}>
                    <div
                        className={css(styles.imgContainer)}
                        style={{
                            backgroundImage: cssUrl(images.map),
                        }}
                    />
                    <View style={styles.right}>
                        <Section
                            title={strings.whatIsIt}
                            description={strings.whatIsItDescription}
                        />
                        <View style={styles.btnContainer}>
                            <Button
                                kind="secondary"
                                href={strings.officialLink}
                            >
                                {strings.ctaLearnMore}
                            </Button>
                        </View>
                        {strings.whyAlign && strings.whyAlignDescription && (
                            <React.Fragment>
                                <Strut size={spacing.xxLarge_48} />
                                <Section
                                    title={strings.whyAlign}
                                    description={strings.whyAlignDescription}
                                />
                            </React.Fragment>
                        )}
                    </View>
                </View>
                <View style={styles.subsection2}>
                    <HeadingLarge style={styles.howCanWeHelp}>
                        {strings.howCanWeHelp}
                    </HeadingLarge>
                    <View style={styles.waysSections}>
                        {strings.waysWeCanHelp.map((column, i) => (
                            <View key={i} style={styles.waysSection}>
                                <HeadingMedium style={styles.headingMedium}>
                                    {column[0]}
                                </HeadingMedium>
                                <View style={styles.bullets}>
                                    {column.slice(1).map((way, i) => (
                                        <View style={styles.bullet} key={i}>
                                            <PhosphorIcon
                                                color={color.green}
                                                size="medium"
                                                icon={checkIcon}
                                                style={styles.checkmark}
                                            />
                                            <Body>{way}</Body>
                                        </View>
                                    ))}
                                </View>
                            </View>
                        ))}
                    </View>
                </View>
            </View>
        );
    }
}

const styles = StyleSheet.create({
    overview: {
        marginTop: 66,
        marginBottom: 66,
        maxWidth: PAGE_WIDTH,
        alignSelf: "center",
        [mediaQueries.mdOrSmaller]: {
            marginLeft: 24,
            marginRight: 24,
            marginTop: 48,
            marginBottom: 24,
        },
    },
    subsection1: {
        flexDirection: "row",
        paddingBottom: 64,
        borderBottom: "1px solid rgba(33, 36, 44, 0.16)",
        [mediaQueries.mdOrSmaller]: {
            flexDirection: "row-reverse",
            paddingBottom: 48,
        },
    },
    subsection2: {
        paddingTop: 64,
        [mediaQueries.mdOrSmaller]: {
            paddingTop: 48,
        },
    },
    imgContainer: {
        width: 240,
        height: 247,
        marginRight: 48,
        borderRadius: 4,
        flexShrink: 0,
        backgroundRepeat: "no-repeat",
        backgroundSize: "contain",
        [mediaQueries.mdOrSmaller]: {
            marginRight: 0,
            marginLeft: 16,
            width: 168,
            height: 128,
            alignSelf: "center",
        },
        [mediaQueries.smOrSmaller]: {
            display: "none",
        },
    },
    howCanWeHelp: {
        marginBottom: 40,
        [mediaQueries.mdOrSmaller]: {
            marginBottom: 24,
        },
    },
    right: {},
    btnContainer: {
        flexDirection: "row",
    },
    waysSections: {
        flexDirection: "row",
        [mediaQueries.mdOrSmaller]: {
            flexDirection: "column",
        },
    },
    waysSection: {
        width: "50%",
        [mediaQueries.mdOrSmaller]: {
            ":not(:first-child)": {
                marginTop: 24,
            },
            width: "auto",
        },
    },
    bullets: {
        marginTop: 24,
        flexGrow: 1,
        flexShrink: 0,
    },
    bullet: {
        flexDirection: "row",
        height: 48,
        justifyContent: "left",
        alignItems: "center",
        flexGrow: 1,
        flexShrink: 0,
        [mediaQueries.mdOrSmaller]: {
            height: "auto",
            marginBottom: 24,
        },
    },
    checkmark: {
        flexShrink: 0,
        marginRight: 16,
    },
    headingMedium: {
        [mediaQueries.mdOrSmaller]: {
            fontSize: 16,
            fontWeight: "bold",
            lineHeight: 1.25,
        },
    },
    withBottomPadding: {
        marginBottom: 24,
    },
});
